import React from "react";
import { Price, PriceFrequencyPlacement, isPresent } from "../../atoms/price/price";
import { Text, type TextBodyElement, type TextBodyVariant } from "../../atoms/text/text";
import styles from "./price-block.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Re-exported so a consumer typing its own `frequencyPlacement` prop (PlanCard,
   for one) imports the type from its direct dependency rather than reaching past
   it into the Price atom. */
export type { PriceFrequencyPlacement } from "../../atoms/price/price";

/**
 * PriceBlock — a price with an optional struck-through previous price, a
 * promotional callout and legal copy. Every text run comes from the Price or
 * Text atom; this module adds layout, the strike line and the colours the atoms
 * cannot express. Server Component. The layout is presence-based and there is
 * no size prop: pass a row's prop and the row appears, at every breakpoint.
 */
/* ⚠ The cue that makes the struck price mean something non-visually. `<s>`
   carries no implicit ARIA role and `text-decoration-line` is not in the
   accessibility tree, so without it the block announces two prices with nothing
   to say which one applies. ⚠ The trailing space is load-bearing: it keeps the
   cue and the amount from being announced as one run. */
export const PREVIOUS_PRICE_CUE = "Regular price ";

export interface PriceBlockProps {
  /** Passed through — the dollar portion, e.g. "$9". Required, as on the atom. */
  dollars: string;
  /** Passed through — the cents portion. e.g. ".99/mo". */
  cents?: string;
  /** Passed through — suffix after the price, e.g. "+ taxes & fees". */
  frequency?: string;
  /**
   * Passed through — where the suffix sits, inline with the price (default) or
   * on its own row below it.
   */
  frequencyPlacement?: PriceFrequencyPlacement;
  /**
   * A previous price shown with a strikethrough above the main price, e.g.
   * "$74.99/mo.". Rendered with a semantic `<s>` element and a red line.
   * Omit for a non-promotional layout.
   */
  strikethroughPrice?: string;
  /**
   * Short callout shown next to the strikethrough, e.g. "$2 off bill". Omit
   * if there is no promotional message.
   */
  secondaryText?: string;
  /**
   * Legal / disclaimer copy shown beneath the price, in a `<p>` so it reads as
   * prose. A ReactNode so a "See details" link can sit inline.
   */
  legal?: React.ReactNode;
  /** The legal copy's Text variant. Defaults to `label`. */
  legalVariant?: TextBodyVariant;
  /** The legal copy's element. Defaults to `p`. */
  legalElement?: TextBodyElement;
  className?: string;
}

function PriceBlockImpl({
  dollars,
  cents,
  frequency,
  frequencyPlacement,
  strikethroughPrice,
  secondaryText,
  legal,
  legalVariant = "label",
  legalElement = "p",
  className
}: PriceBlockProps) {
  // Derived once each, so the layout flag and the render guards can never
  // disagree about whether a row exists.
  const hasStrikethrough = isPresent(strikethroughPrice);
  const hasSecondary = isPresent(secondaryText);
  const hasLegal = isPresent(legal);
  const hasAboveRow = hasStrikethrough || hasSecondary;
  const hasComplex = hasAboveRow || hasLegal;

  const classes = [
    styles.root,
    hasComplex ? styles.complex : styles.basic,
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      {hasAboveRow && (
        <div className={styles.above}>
          {hasStrikethrough && (
            <s className={styles.strikethrough}>
              {/* See PREVIOUS_PRICE_CUE above. */}
              <span className="dds-sr-only">{PREVIOUS_PRICE_CUE}</span>
              <Price className={styles.strikePrice} value={strikethroughPrice.trim()} />
            </s>
          )}
          {hasSecondary && (
            <Text as="span" className={styles.secondary} variant="body-3" weight="medium">
              {secondaryText}
            </Text>
          )}
        </div>
      )}

      <Price
        cents={cents}
        dollars={dollars}
        frequency={frequency}
        frequencyPlacement={frequencyPlacement}
      />

      {hasLegal && (
        <Text as={legalElement} className={styles.legal} variant={legalVariant}>
          {legal}
        </Text>
      )}
    </div>
  );
}
export const PriceBlock = withSafeRender(PriceBlockImpl, "PriceBlock");

export default PriceBlock;
